import {
  IonAlert,
  IonButton,
  IonIcon,
  IonInput,
  IonSpinner,
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import {
  alertCircleOutline,
  banOutline,
  createOutline,
  linkOutline,
  lockClosedOutline,
  lockOpenOutline,
  searchOutline,
  trashOutline,
} from 'ionicons/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchPickerModal } from '../SearchPickerModal.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useEnqueueSupplierItemMatchManagement,
  useMasters,
} from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { normalizeUom } from '../../lib/uom.js';
import { toUserMessage } from '../../lib/errors.js';
import {
  buildSupplierItemMatchRows,
  supplierMatchSourceLabel,
  supplierPackFactorLabel,
  type SupplierItemAliasRecord,
  type SupplierItemMatchRow,
} from '../../lib/suppliers/supplierItemMatches.js';

interface SupplierFoodRow {
  id: string;
  name: string;
  default_uom?: string | null;
  archived_at?: string | null;
}

function hasDestinationUomIssue(
  row: SupplierItemMatchRow,
  foodById: ReadonlyMap<string, SupplierFoodRow>,
): boolean {
  const destinationUom = normalizeUom(foodById.get(row.foodId)?.default_uom);
  return !destinationUom || row.baseUoms.some(
    (baseUom) => normalizeUom(baseUom) !== destinationUom,
  );
}

interface SupplierImportPreference {
  preference_key: string;
  raw_description_norm: string;
  supplier_code: string | null;
  updated_at: string;
}

interface SupplierItemMatchesPanelProps {
  supplierId: string;
  isOpen: boolean;
  onRequestCanonicalMerge: (sourceFoodId: string, targetFoodId: string) => void;
}

const PAGE_SIZE = 1_000;

async function listSupplierAliases(
  tenantId: string,
  supplierId: string,
): Promise<SupplierItemAliasRecord[]> {
  const rows: SupplierItemAliasRecord[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await getSupabase()
      .from('supplier_item_aliases')
      .select(
        'raw_description_norm, food_id, supplier_code, occurrences, last_used_at, source, pack_factor, pack_uom, base_uom',
      )
      .eq('tenant_id', tenantId)
      .eq('supplier_id', supplierId)
      .order('last_used_at', { ascending: false })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const page = (data ?? []) as SupplierItemAliasRecord[];
    rows.push(...page);
    if (page.length < PAGE_SIZE) break;
  }
  return rows;
}

async function listSupplierImportSkips(
  tenantId: string,
  supplierId: string,
): Promise<SupplierImportPreference[]> {
  const { data, error } = await getSupabase()
    .from('supplier_item_import_preferences')
    .select('preference_key, raw_description_norm, supplier_code, updated_at')
    .eq('tenant_id', tenantId)
    .eq('supplier_id', supplierId)
    .eq('action', 'skip')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as SupplierImportPreference[];
}

function preferenceLabel(value: string): string {
  const trimmed = value.trim();
  return trimmed
    ? `${trimmed.charAt(0).toLocaleUpperCase('it-IT')}${trimmed.slice(1)}`
    : 'Descrizione non disponibile';
}

function formatLastUsed(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'data non disponibile';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function SupplierItemMatchesPanel(
  { supplierId, isOpen, onRequestCanonicalMerge }: SupplierItemMatchesPanelProps,
): JSX.Element {
  const { profile } = useAuth();
  const tenantId = profile?.active_tenant_id ?? null;
  const [search, setSearch] = useState('');
  const [removingSkipKey, setRemovingSkipKey] = useState<string | null>(null);
  const [skipFeedback, setSkipFeedback] = useState<string | null>(null);
  const [managementUnlocked, setManagementUnlocked] = useState(false);
  const [unlockWarningOpen, setUnlockWarningOpen] = useState(false);
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);
  const openSecondUnlockRef = useRef(false);
  const [editingRow, setEditingRow] = useState<SupplierItemMatchRow | null>(null);
  const [deletingRow, setDeletingRow] = useState<SupplierItemMatchRow | null>(null);
  const [managedRowKeys, setManagedRowKeys] = useState<Set<string>>(new Set());
  const [deletedRowKeys, setDeletedRowKeys] = useState<Set<string>>(new Set());
  const [managementFeedback, setManagementFeedback] = useState<string | null>(null);
  const manageMatch = useEnqueueSupplierItemMatchManagement();
  const { data: foods = [] } = useMasters<SupplierFoodRow>('foods', {
    includeArchived: true,
  });
  const aliasesQuery = useQuery({
    queryKey: ['supplier-item-aliases', tenantId, supplierId],
    enabled: isOpen && !!tenantId && !!supplierId,
    queryFn: () => listSupplierAliases(tenantId!, supplierId),
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
  const importSkipsQuery = useQuery({
    queryKey: ['supplier-item-import-skips', tenantId, supplierId],
    enabled: isOpen && !!tenantId && !!supplierId,
    queryFn: () => listSupplierImportSkips(tenantId!, supplierId),
    staleTime: 30_000,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    setManagementUnlocked(false);
    setUnlockWarningOpen(false);
    setUnlockConfirmOpen(false);
    openSecondUnlockRef.current = false;
    setEditingRow(null);
    setDeletingRow(null);
    setManagedRowKeys(new Set());
    setDeletedRowKeys(new Set());
    setManagementFeedback(null);
  }, [isOpen, supplierId]);

  async function removeImportSkip(preference: SupplierImportPreference): Promise<void> {
    if (!tenantId || removingSkipKey) return;
    setRemovingSkipKey(preference.preference_key);
    setSkipFeedback(null);
    try {
      const { error } = await getSupabase().rpc('set_supplier_item_import_skip', {
        p_tenant_id: tenantId,
        p_supplier_id: supplierId,
        p_raw_description: preference.raw_description_norm,
        p_supplier_code: preference.supplier_code,
        p_skip: false,
      });
      if (error) throw error;
      await importSkipsQuery.refetch();
      setSkipFeedback('Esclusione rimossa: il prodotto verrà verificato al prossimo documento.');
    } catch {
      setSkipFeedback('Non sono riuscito a rimuovere l’esclusione. Controlla la connessione e riprova.');
    } finally {
      setRemovingSkipKey(null);
    }
  }

  const foodById = useMemo(
    () => new Map(foods.map((food) => [food.id, food])),
    [foods],
  );
  const rows = useMemo(
    () => buildSupplierItemMatchRows(aliasesQuery.data ?? []),
    [aliasesQuery.data],
  );
  const visibleRows = useMemo(
    () => rows.filter((row) => !deletedRowKeys.has(row.key)),
    [deletedRowKeys, rows],
  );
  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('it-IT');
    if (!query) return visibleRows;
    return visibleRows.filter((row) => {
      const food = foodById.get(row.foodId);
      return [
        row.supplierCode,
        food?.name,
        ...row.descriptions,
      ].some((value) => value?.toLocaleLowerCase('it-IT').includes(query));
    });
  }, [foodById, search, visibleRows]);
  const replacementFoods = useMemo(() => {
    if (
      !editingRow
      || editingRow.hasBaseUomConflict
      || hasDestinationUomIssue(editingRow, foodById)
    ) {
      return [];
    }
    const requiredUom = normalizeUom(foodById.get(editingRow.foodId)?.default_uom);
    return foods
      .filter((food) => (
        !food.archived_at
        && food.id !== editingRow.foodId
        && normalizeUom(food.default_uom) === requiredUom
      ))
      .sort((left, right) => left.name.localeCompare(right.name, 'it'));
  }, [editingRow, foodById, foods]);

  function selectorFor(row: SupplierItemMatchRow): {
    supplierCode: string | null;
    rawDescriptionNorm: string | null;
  } {
    return row.supplierCode
      ? { supplierCode: row.supplierCode, rawDescriptionNorm: null }
      : { supplierCode: null, rawDescriptionNorm: row.descriptions[0] ?? null };
  }

  async function queueMatchDelete(row: SupplierItemMatchRow): Promise<void> {
    if (!tenantId || !profile?.active_business_id) {
      setManagementFeedback('Seleziona il locale attivo prima di modificare il collegamento.');
      return;
    }
    if (manageMatch.isPending || managedRowKeys.has(row.key)) return;
    setManagementFeedback(null);
    try {
      await manageMatch.mutateAsync({
        requestId: crypto.randomUUID(),
        tenantId,
        businessId: profile.active_business_id,
        supplierId,
        foodId: row.foodId,
        ...selectorFor(row),
        action: 'delete',
        targetFoodId: null,
        requestedAt: new Date().toISOString(),
      });
      setManagedRowKeys((current) => new Set(current).add(row.key));
      setDeletedRowKeys((current) => new Set(current).add(row.key));
      setManagementFeedback('Collegamento eliminato e sincronizzazione avviata.');
    } catch (error) {
      setManagementFeedback(toUserMessage(
        error,
        'Modifica non registrata. Controlla i dati e riprova.',
      ));
    }
  }

  return (
    <>
      <section className="te-supplier-matches te-supplier-skips" aria-labelledby="supplier-skips-title">
        <div className="te-supplier-matches__heading">
          <span className="te-supplier-matches__icon" aria-hidden="true">
            <IonIcon icon={banOutline} />
          </span>
          <div>
            <h2 id="supplier-skips-title">Articoli esclusi dall’import</h2>
            <p>Vengono saltati automaticamente nei prossimi documenti del fornitore.</p>
          </div>
          {!importSkipsQuery.isLoading && !importSkipsQuery.isError && (
            <span className="te-supplier-matches__count">
              {(importSkipsQuery.data ?? []).length}
            </span>
          )}
        </div>

        {importSkipsQuery.isLoading && (
          <div className="te-supplier-matches__state" role="status">
            <IonSpinner name="crescent" />
            Carico le esclusioni…
          </div>
        )}

        {importSkipsQuery.isError && (
          <div className="te-supplier-matches__state is-error" role="alert">
            <IonIcon icon={alertCircleOutline} aria-hidden="true" />
            <span>Esclusioni non disponibili. Controlla la connessione e riprova.</span>
            <IonButton fill="outline" size="small" onClick={() => void importSkipsQuery.refetch()}>
              Riprova
            </IonButton>
          </div>
        )}

        {!importSkipsQuery.isLoading && !importSkipsQuery.isError && (importSkipsQuery.data ?? []).length === 0 && (
          <div className="te-supplier-matches__state">
            Nessun articolo escluso. Le scelte “Salta import” compariranno qui.
          </div>
        )}

        {!importSkipsQuery.isLoading && !importSkipsQuery.isError && (importSkipsQuery.data ?? []).length > 0 && (
          <div className="te-supplier-matches__list te-supplier-skips__list">
            {(importSkipsQuery.data ?? []).map((preference) => (
              <article className="te-supplier-match te-supplier-skip" key={preference.preference_key}>
                <div className="te-supplier-match__topline">
                  <strong>{preferenceLabel(preference.raw_description_norm)}</strong>
                  {preference.supplier_code && (
                    <span className="te-supplier-match__code">Cod. {preference.supplier_code}</span>
                  )}
                </div>
                <p>Escluso dal carico di magazzino · aggiornato il {formatLastUsed(preference.updated_at)}</p>
                <IonButton
                  fill="outline"
                  size="small"
                  disabled={removingSkipKey !== null}
                  aria-label={`Rimuovi esclusione di ${preference.raw_description_norm}`}
                  onClick={() => { void removeImportSkip(preference); }}
                >
                  {removingSkipKey === preference.preference_key ? 'Attendi…' : 'Rimuovi esclusione'}
                </IonButton>
              </article>
            ))}
          </div>
        )}

        {skipFeedback && (
          <div className="te-supplier-skips__feedback" role="status" aria-live="polite">
            {skipFeedback}
          </div>
        )}
      </section>

      <section className="te-supplier-matches" aria-labelledby="supplier-matches-title">
      <div className="te-supplier-matches__heading">
        <span className="te-supplier-matches__icon" aria-hidden="true">
          <IonIcon icon={linkOutline} />
        </span>
        <div>
          <h2 id="supplier-matches-title">Codici articolo collegati</h2>
          <p>Panoramica dei match riutilizzati durante l’import DDT.</p>
        </div>
        {!aliasesQuery.isLoading && !aliasesQuery.isError && (
          <span className="te-supplier-matches__count">
            {visibleRows.length}
          </span>
        )}
      </div>

      {!aliasesQuery.isLoading && !aliasesQuery.isError && rows.length > 0 && (
        <div className={`te-supplier-matches__guard${managementUnlocked ? ' is-unlocked' : ''}`}>
          <div>
            <strong>
              <IonIcon
                icon={managementUnlocked ? lockOpenOutline : lockClosedOutline}
                aria-hidden="true"
              />
              {managementUnlocked ? 'Gestione sbloccata' : 'Modifica protetta'}
            </strong>
            <span>
              {managementUnlocked
                ? 'Puoi riunire gli articoli collegati o eliminare un singolo match.'
                : 'Servono due conferme prima di modificare la memoria degli import.'}
            </span>
          </div>
          {!managementUnlocked && (
            <IonButton
              fill="outline"
              size="small"
              onClick={() => setUnlockWarningOpen(true)}
            >
              Gestisci collegamenti
            </IonButton>
          )}
        </div>
      )}

      {aliasesQuery.isLoading && (
        <div className="te-supplier-matches__state" role="status">
          <IonSpinner name="crescent" />
          Carico i collegamenti…
        </div>
      )}

      {aliasesQuery.isError && (
        <div className="te-supplier-matches__state is-error" role="alert">
          <IonIcon icon={alertCircleOutline} aria-hidden="true" />
          <span>Collegamenti non disponibili. Controlla la connessione e riprova.</span>
          <IonButton fill="outline" size="small" onClick={() => void aliasesQuery.refetch()}>
            Riprova
          </IonButton>
        </div>
      )}

      {!aliasesQuery.isLoading && !aliasesQuery.isError && visibleRows.length === 0 && (
        <div className="te-supplier-matches__state">
          {rows.length === 0
            ? 'Nessun codice ancora collegato. I match compariranno dopo la prima importazione confermata.'
            : 'Tutti i collegamenti visibili sono stati eliminati e sono in sincronizzazione.'}
        </div>
      )}

      {!aliasesQuery.isLoading && !aliasesQuery.isError && visibleRows.length > 0 && (
        <>
          <div className="te-supplier-matches__search">
            <IonIcon icon={searchOutline} aria-hidden="true" />
            <IonInput
              aria-label="Cerca nei codici articolo collegati"
              placeholder="Cerca codice, alimento o descrizione"
              value={search}
              onIonInput={(event) => setSearch(String(event.detail.value ?? ''))}
              clearInput
            />
          </div>
          <div className="te-supplier-matches__list">
            {filteredRows.map((row) => {
              const food = foodById.get(row.foodId);
              const packLabel = supplierPackFactorLabel(row);
              return (
                <article className="te-supplier-match" key={row.key}>
                  <div className="te-supplier-match__topline">
                    <strong>{food?.name ?? 'Alimento non disponibile'}</strong>
                    {row.supplierCode && (
                      <span className="te-supplier-match__code">
                        Cod. {row.supplierCode}
                      </span>
                    )}
                  </div>
                  {food?.archived_at && (
                    <span className="te-supplier-match__archived">Alimento archiviato</span>
                  )}
                  <p>
                    Sul DDT: {row.descriptions[0] ?? 'descrizione non disponibile'}
                    {row.descriptions.length > 1
                      ? ` · altre ${row.descriptions.length - 1} diciture`
                      : ''}
                  </p>
                  <div className="te-supplier-match__meta">
                    <span>{supplierMatchSourceLabel(row.source)}</span>
                    <span>Usato {row.occurrences} {row.occurrences === 1 ? 'volta' : 'volte'}</span>
                    <span>Ultimo: {formatLastUsed(row.lastUsedAt)}</span>
                  </div>
                  {packLabel && (
                    <div className="te-supplier-match__conversion">
                      Conversione: <strong>{packLabel}</strong>
                    </div>
                  )}
                  {row.hasCodeConflict && (
                    <div className="te-supplier-match__conflict" role="alert">
                      <IonIcon icon={alertCircleOutline} aria-hidden="true" />
                      Questo codice risulta collegato a più alimenti: verifica prima del prossimo import.
                    </div>
                  )}
                  {(row.hasBaseUomConflict || hasDestinationUomIssue(row, foodById)) && (
                    <div className="te-supplier-match__conflict" role="alert">
                      <IonIcon icon={alertCircleOutline} aria-hidden="true" />
                      Il gruppo contiene unità di destinazione incoerenti: puoi eliminarlo, ma non riassegnarlo in blocco.
                    </div>
                  )}
                  {managementUnlocked && (
                    <div className="te-supplier-match__actions">
                      <IonButton
                        fill="outline"
                        size="small"
                        disabled={
                          manageMatch.isPending
                          || managedRowKeys.has(row.key)
                          || row.hasBaseUomConflict
                          || hasDestinationUomIssue(row, foodById)
                        }
                        aria-label={`Riunisci articolo di ${row.supplierCode ?? row.descriptions[0] ?? 'articolo'}`}
                        onClick={() => setEditingRow(row)}
                      >
                        <IonIcon icon={createOutline} slot="start" />
                        Riunisci articoli
                      </IonButton>
                      <IonButton
                        fill="outline"
                        color="danger"
                        size="small"
                        disabled={manageMatch.isPending || managedRowKeys.has(row.key)}
                        aria-label={`Elimina collegamento di ${row.supplierCode ?? row.descriptions[0] ?? 'articolo'}`}
                        onClick={() => setDeletingRow(row)}
                      >
                        <IonIcon icon={trashOutline} slot="start" />
                        Elimina
                      </IonButton>
                    </div>
                  )}
                  {managedRowKeys.has(row.key) && (
                    <div className="te-supplier-match__pending" role="status">
                      Modifica in sincronizzazione
                    </div>
                  )}
                </article>
              );
            })}
          </div>
          {filteredRows.length === 0 && (
            <div className="te-supplier-matches__state">
              Nessun collegamento corrisponde alla ricerca.
            </div>
          )}
        </>
      )}
      {managementFeedback && (
        <div className="te-supplier-matches__feedback" role="status" aria-live="polite">
          {managementFeedback}
        </div>
      )}
      </section>

      <IonAlert
        isOpen={unlockWarningOpen}
        header="Gestione protetta"
        message="Stai per eliminare un match oppure riunire due articoli. La fusione riallinea giacenze, tutti i legami fornitore e le ricette correnti; documenti e movimenti storici restano immutati."
        buttons={[
          { text: 'Annulla', role: 'cancel' },
          {
            text: 'Continua',
            handler: () => {
              openSecondUnlockRef.current = true;
              setUnlockWarningOpen(false);
            },
          },
        ]}
        onDidDismiss={() => {
          setUnlockWarningOpen(false);
          if (openSecondUnlockRef.current) {
            openSecondUnlockRef.current = false;
            requestAnimationFrame(() => setUnlockConfirmOpen(true));
          }
        }}
      />
      <IonAlert
        isOpen={unlockConfirmOpen}
        header="Seconda verifica"
        message="Conferma di aver controllato codice, descrizione e articolo associato. Dopo lo sblocco, la fusione richiederà comunque una conferma finale con il riepilogo completo."
        buttons={[
          { text: 'Annulla', role: 'cancel' },
          {
            text: 'Sblocca modifica',
            handler: () => {
              setManagementUnlocked(true);
              setManagementFeedback('Gestione dei collegamenti sbloccata per questa scheda.');
            },
          },
        ]}
        onDidDismiss={() => setUnlockConfirmOpen(false)}
      />
      <IonAlert
        isOpen={!!deletingRow}
        header="Elimina collegamento"
        message={deletingRow
          ? `Eliminare il match “${deletingRow.supplierCode ?? deletingRow.descriptions[0] ?? 'articolo'}” con “${foodById.get(deletingRow.foodId)?.name ?? 'alimento non disponibile'}”? Al prossimo documento dovrà essere associato nuovamente.`
          : ''}
        buttons={[
          { text: 'Annulla', role: 'cancel' },
          {
            text: 'Elimina match',
            role: 'destructive',
            handler: () => {
              const row = deletingRow;
              setDeletingRow(null);
              if (row) void queueMatchDelete(row);
            },
          },
        ]}
        onDidDismiss={() => setDeletingRow(null)}
      />
      <SearchPickerModal
        isOpen={!!editingRow}
        title="Scegli l’articolo canonico"
        items={replacementFoods.map((food) => ({
          id: food.id,
          label: food.name,
          sublabel: food.default_uom ? `UdM destinazione: ${food.default_uom}` : undefined,
        }))}
        selectedId={editingRow?.foodId}
        searchPlaceholder="Cerca alimento…"
        emptyHint="Nessun alimento attivo con un’unità di destinazione compatibile."
        onSelect={(foodId) => {
          const row = editingRow;
          setEditingRow(null);
          if (!row) return;
          onRequestCanonicalMerge(row.foodId, foodId);
        }}
        onClose={() => setEditingRow(null)}
      />
    </>
  );
}
